import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import {
    buildDoceboAsyncDataCacheKey,
    fetchWithDoceboAccessToken,
    useDoceboAccessToken
} from '~/composables/useDoceboAccessToken';
import type { AcademyTrainingsListingResponse } from '~/types/academy/academy';
import {
    buildAcademyTrainingsListingQuery,
    type AcademyTrainingsListingFilters
} from '~/utils/academy/trainings/buildTrainingsListingQuery';

function fetchAcademyTrainingsListing(
    filters: AcademyTrainingsListingFilters | undefined,
    headers: HeadersInit,
    signal?: AbortSignal
): Promise<AcademyTrainingsListingResponse> {
    return $fetch<AcademyTrainingsListingResponse>('/api/academy/trainings/all', {
        timeout: 10_000,
        query: buildAcademyTrainingsListingQuery(filters),
        headers,
        signal
    });
}

export function useFetchAcademyTrainingsListing() {
    const headers = useRequestHeaders(['cookie']);
    const { hasDoceboAccessToken } = useDoceboAccessToken();

    return (filters?: AcademyTrainingsListingFilters, signal?: AbortSignal) =>
        fetchWithDoceboAccessToken(hasDoceboAccessToken.value, () =>
            fetchAcademyTrainingsListing(filters, headers, signal)
        );
}

export function useAcademyTrainingsListingFetch(
    filters?: MaybeRefOrGetter<AcademyTrainingsListingFilters | undefined>
) {
    const { hasDoceboAccessToken } = useDoceboAccessToken();
    const query = computed(() => buildAcademyTrainingsListingQuery(toValue(filters)));
    const querySignature = computed(() => JSON.stringify(query.value));
    const fetchListing = useFetchAcademyTrainingsListing();

    // Scoped per instance so multiple components on the same page will not abort each other's requests.
    let activeListingRequest: AbortController | undefined;
    const beginListingRequest = (externalSignal?: AbortSignal): AbortSignal => {
        activeListingRequest?.abort();

        const controller = new AbortController();
        activeListingRequest = controller;

        if (externalSignal) {
            if (externalSignal.aborted) {
                controller.abort();
            } else {
                externalSignal.addEventListener('abort', () => controller.abort(), { once: true });
            }
        }

        return controller.signal;
    };

    return useAsyncData(
        () => buildDoceboAsyncDataCacheKey('academy-trainings-listing', query.value, hasDoceboAccessToken.value),
        (_nuxtApp, { signal }) => fetchListing(toValue(filters), beginListingRequest(signal)),
        {
            watch: [querySignature, hasDoceboAccessToken],
            dedupe: 'defer'
        }
    );
}
